import { useEffect, useMemo, useState } from 'react';
import {
  Modal, TextInput, Textarea, NumberInput, Stack, Group, Button, Alert, Paper, Table, Text, Loader, Center
} from '@mantine/core';
import { IconAdd as IconPlus, IconChip as IconTrash } from '../../components/icons/CncIcons';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import ImageUpload from '../../components/ImageUpload';
import MarkupSlider from '../../components/MarkupSlider';
import { api, imageUrl } from '../../api/client';
import { useAuth, canWrite, isAdmin } from '../../store/auth';
import SelectAccessoryModal from './SelectAccessoryModal';
import SelectConsumableModal from './SelectConsumableModal';
import AccessoryModal from '../accessories/AccessoryModal';
import type { Accessory } from '../accessories/AccessoriesPage';
import StatusLogModal, { type StatusLogEntry } from './StatusLogModal';
import DangerousDeleteModal from './DangerousDeleteModal';

interface EquipmentFull {
  id: number;
  name: string;
  manufacturer: string | null;
  model: string | null;
  service: string | null;
  capabilities: string | null;
  imagePath: string | null;
  powerKwh: number | null;
  valueEur: number | null;
  serviceLifeHours: number | null;
  markupPct: number;
  depreciationPerHour: number | null;
  hourlyRateEur: number | null;
  status: 'Available' | 'Broken' | 'Dead';
  accessories: Accessory[];
  consumables: { id: number; name: string; imagePath: string | null; typicalPriceEur: number | null }[];
  statusLog: StatusLogEntry[];
}

export default function EquipmentModal({
  id,
  onClose,
  onSaved,
  onDeleted,
}: {
  id: number | null;
  onClose: () => void;
  onSaved: () => void;
  onDeleted: () => void;
}) {
  const { me } = useAuth();
  const writable = canWrite(me?.role);
  const admin = isAdmin(me?.role);

  const [loading, setLoading] = useState(!!id);
  const [data, setData] = useState<EquipmentFull | null>(null);

  // Basics
  const [name, setName] = useState('');
  const [manufacturer, setManufacturer] = useState('');
  const [model, setModel] = useState('');
  const [service, setService] = useState('');
  const [capabilities, setCapabilities] = useState('');
  const [image, setImage] = useState<string | null>(null);

  // Running Costs
  const [powerKwh, setPowerKwh] = useState<number | string>('');
  const [valueEur, setValueEur] = useState<number | string>('');
  const [serviceLifeHours, setServiceLifeHours] = useState<number | string>('');
  const [markup, setMarkup] = useState(30);

  // nested editors
  const [editingAccessoryId, setEditingAccessoryId] = useState<number | null>(null);
  const [selectingAccessory, setSelectingAccessory] = useState(false);
  const [selectingConsumable, setSelectingConsumable] = useState(false);
  const [editingStatusLog, setEditingStatusLog] = useState<StatusLogEntry | 'new' | null>(null);
  const [dangerOpen, setDangerOpen] = useState(false);

  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function refresh() {
    if (!id) return;
    const full = await api.get<EquipmentFull>(`/equipment/${id}`);
    setData(full);
    setName(full.name);
    setManufacturer(full.manufacturer ?? '');
    setModel(full.model ?? '');
    setService(full.service ?? '');
    setCapabilities(full.capabilities ?? '');
    setImage(full.imagePath);
    setPowerKwh(full.powerKwh ?? '');
    setValueEur(full.valueEur ?? '');
    setServiceLifeHours(full.serviceLifeHours ?? '');
    setMarkup(full.markupPct);
  }

  useEffect(() => {
    if (!id) return;
    (async () => {
      try { await refresh(); } finally { setLoading(false); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const computed = useMemo(() => {
    const v = typeof valueEur === 'number' ? valueEur : Number(valueEur);
    const h = typeof serviceLifeHours === 'number' ? serviceLifeHours : Number(serviceLifeHours);
    const p = typeof powerKwh === 'number' ? powerKwh : Number(powerKwh);
    const depreciation = v && h ? v / h : null;
    const base = (p || 0) + (depreciation || 0);
    const rate = (base || depreciation) ? base * (1 + markup / 100) : null;
    return { depreciation, rate };
  }, [valueEur, serviceLifeHours, powerKwh, markup]);

  function confirmClose() {
    onClose();
  }

  async function save() {
    setErr(null);
    if (!name.trim()) return setErr('Name is required.');
    if (powerKwh !== '' && Number(powerKwh) < 0.1) return setErr('Power must be at least 0.1 kW/h.');
    if (valueEur !== '' && Number(valueEur) < 1) return setErr('Value must be at least 1 EUR.');
    setBusy(true);
    try {
      const body = {
        name: name.trim(),
        manufacturer: manufacturer || null,
        model: model || null,
        service: service.trim() || null,
        capabilities: capabilities || null,
        imagePath: image,
        powerKwh: powerKwh === '' ? null : Number(powerKwh),
        valueEur: valueEur === '' ? null : Number(valueEur),
        serviceLifeHours: serviceLifeHours === '' ? null : Number(serviceLifeHours),
        markupPct: markup,
      };
      if (id) await api.put(`/equipment/${id}`, body);
      else await api.post('/equipment', body);
      notifications.show({ message: 'Equipment saved.', color: 'green' });
      onSaved();
    } catch (e: any) {
      setErr(e.message ?? 'Save failed');
    } finally {
      setBusy(false);
    }
  }

  async function detachConsumable(consId: number) {
    if (!id) return;
    await api.delete(`/equipment/${id}/consumables/${consId}`);
    await refresh();
  }

  async function detachAccessory(accId: number) {
    if (!id) return;
    await api.delete(`/equipment/${id}/accessories/${accId}`);
    await refresh();
  }

  async function removeStatusLog(logId: number) {
    if (!id) return;
    modals.openConfirmModal({
      title: 'Delete this status log entry?',
      labels: { confirm: 'Delete', cancel: 'Cancel' },
      confirmProps: { color: 'red' },
      onConfirm: async () => {
        await api.delete(`/equipment/${id}/status-log/${logId}`);
        await refresh();
      },
    });
  }

  const titleText = id ? `Edit ${data?.name ?? 'Equipment'}` : 'Add Equipment';
  const titleNode = <span className="modal-title-custom">{titleText}</span>;
  const needsSaveFirst = !id;
  const saveFirstNotice = (
    <div className="section-stub">
      Save the equipment first — once created, you can manage this section here.
    </div>
  );

  return (
    <Modal opened onClose={confirmClose} title={titleNode} size="xl">
      {loading ? (
        <Center p="xl"><Loader /></Center>
      ) : (
        <Stack>
          {/* ----------- Basics ----------- */}
          <div className="section">
            <div className="section-header">Equipment Basics</div>
            <div className="section-body">
              <ImageUpload entity="equipment" value={image} onChange={setImage} />
              <TextInput label="Name" value={name} onChange={(e) => setName(e.currentTarget.value)} required maxLength={120} disabled={!writable} data-tour="equipment-name" />
              <Group grow>
                <TextInput label="Manufacturer" value={manufacturer} onChange={(e) => setManufacturer(e.currentTarget.value)} disabled={!writable} />
                <TextInput label="Model" value={model} onChange={(e) => setModel(e.currentTarget.value)} disabled={!writable} />
              </Group>
              <TextInput
                data-tour="equipment-service"
                label="Service"
                placeholder="e.g. 3D Printing, CNC, Injection"
                value={service}
                onChange={(e) => setService(e.currentTarget.value)}
                maxLength={120}
                disabled={!writable}
                description="The operation type this machine represents. Shown as the “Services” column in the SharePoint Order Tracker."
              />
              <Textarea
                label="Capabilities"
                value={capabilities}
                onChange={(e) => setCapabilities(e.currentTarget.value)}
                maxLength={500}
                autosize
                minRows={3}
                description={`${capabilities.length}/500`}
                disabled={!writable}
              />
              <Text size="xs" c="dimmed">
                Status is derived from the most recent entry in the Equipment Life section below.
              </Text>
            </div>
          </div>

          {/* ----------- Running Costs ----------- */}
          <div className="section">
            <div className="section-header">Running Costs</div>
            <div className="section-body">
              <Group grow>
                <NumberInput label="Power Consumption (kW/h)" value={powerKwh} onChange={setPowerKwh} min={0.1} step={0.1} decimalScale={2} disabled={!writable} data-tour="equipment-power" />
                <NumberInput label="Value (EUR)" value={valueEur} onChange={setValueEur} min={1} step={1} decimalScale={2} disabled={!writable} data-tour="equipment-value" />
              </Group>
              <NumberInput
                data-tour="equipment-life"
                label="Service Life (hours)"
                value={serviceLifeHours}
                onChange={setServiceLifeHours}
                min={1}
                step={1}
                disabled={!writable}
                description="If not known: depreciation period in years × 250 days × 8 hours."
              />
              <MarkupSlider value={markup} onChange={setMarkup} disabled={!writable} />
              <Group grow>
                <Stack gap={4}>
                  <Text size="sm" fw={500}>Depreciation (EUR/hour)</Text>
                  <Paper withBorder p="xs"><Text fw={600}>{computed.depreciation != null ? `€ ${computed.depreciation.toFixed(4)}` : '—'}</Text></Paper>
                </Stack>
                <Stack gap={4}>
                  <Text size="sm" fw={500}>Hourly Rate (EUR/hour)</Text>
                  <Paper withBorder p="xs"><Text fw={600} c="orange">{computed.rate != null ? `€ ${computed.rate.toFixed(2)}` : '—'}</Text></Paper>
                </Stack>
              </Group>
            </div>
          </div>

          {/* ----------- Accessories ----------- */}
          <div className="section">
            <div className="section-header">Typical Accessories</div>
            {needsSaveFirst ? saveFirstNotice : (
              <Table>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th style={{ width: 60 }}>Image</Table.Th>
                    <Table.Th>Name</Table.Th>
                    <Table.Th>Manufacturer</Table.Th>
                    <Table.Th>Model</Table.Th>
                    <Table.Th>Hourly Rate</Table.Th>
                    <Table.Th style={{ width: 110 }}>Remove</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {data?.accessories.map((a) => (
                    <Table.Tr key={a.id} className="click-row" onClick={() => setEditingAccessoryId(a.id)}>
                      <Table.Td>
                        <div className="image-box">{a.imagePath && <img src={imageUrl(a.imagePath, true)} alt={a.name} />}</div>
                      </Table.Td>
                      <Table.Td>{a.name}</Table.Td>
                      <Table.Td>{a.manufacturer ?? '—'}</Table.Td>
                      <Table.Td>{a.model ?? '—'}</Table.Td>
                      <Table.Td>{a.hourlyRateEur != null ? `€ ${a.hourlyRateEur.toFixed(2)}` : '—'}</Table.Td>
                      <Table.Td>
                        <Button size="xs" variant="light" color="red" leftSection={<IconTrash size={12} />}
                          onClick={(e) => { e.stopPropagation(); detachAccessory(a.id); }} disabled={!writable}>
                          Detach
                        </Button>
                      </Table.Td>
                    </Table.Tr>
                  ))}
                  <Table.Tr>
                    <Table.Td colSpan={6}>
                      <Button fullWidth variant="light" leftSection={<IconPlus size={14} />} onClick={() => setSelectingAccessory(true)} disabled={!writable}>
                        Add Accessory
                      </Button>
                    </Table.Td>
                  </Table.Tr>
                </Table.Tbody>
              </Table>
            )}
          </div>

          {/* ----------- Typical Consumables ----------- */}
          <div className="section">
            <div className="section-header">Typical Consumables</div>
            {needsSaveFirst ? saveFirstNotice : (
              <Table>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th style={{ width: 60 }}>Image</Table.Th>
                    <Table.Th>Name</Table.Th>
                    <Table.Th>Typical Price</Table.Th>
                    <Table.Th style={{ width: 100 }}>Remove</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {data?.consumables.map((c) => (
                    <Table.Tr key={c.id}>
                      <Table.Td>
                        <div className="image-box">{c.imagePath && <img src={imageUrl(c.imagePath, true)} alt={c.name} />}</div>
                      </Table.Td>
                      <Table.Td>{c.name}</Table.Td>
                      <Table.Td>{c.typicalPriceEur != null ? `€ ${c.typicalPriceEur.toFixed(2)}` : '—'}</Table.Td>
                      <Table.Td>
                        <Button size="xs" variant="light" color="red" leftSection={<IconTrash size={12} />} onClick={() => detachConsumable(c.id)} disabled={!writable}>
                          Remove
                        </Button>
                      </Table.Td>
                    </Table.Tr>
                  ))}
                  <Table.Tr>
                    <Table.Td colSpan={4}>
                      <Button fullWidth variant="light" leftSection={<IconPlus size={14} />} onClick={() => setSelectingConsumable(true)} disabled={!writable}>
                        Add Consumable
                      </Button>
                    </Table.Td>
                  </Table.Tr>
                </Table.Tbody>
              </Table>
            )}
          </div>

          {/* ----------- Equipment Life ----------- */}
          <div className="section">
            <div className="section-header">Equipment Life</div>
            {needsSaveFirst ? saveFirstNotice : (
              <Table>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Date</Table.Th>
                    <Table.Th>Status</Table.Th>
                    <Table.Th>Edited by</Table.Th>
                    <Table.Th style={{ width: 100 }}>Delete</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {data?.statusLog.map((l) => (
                    <Table.Tr key={l.id} className="click-row" onClick={() => setEditingStatusLog(l)}>
                      <Table.Td>{l.eventDate}</Table.Td>
                      <Table.Td>{l.status}</Table.Td>
                      <Table.Td>{l.editedByUsername ?? '—'}</Table.Td>
                      <Table.Td>
                        <Button size="xs" variant="light" color="red" leftSection={<IconTrash size={12} />}
                          onClick={(e) => { e.stopPropagation(); removeStatusLog(l.id); }} disabled={!writable}>
                          Delete
                        </Button>
                      </Table.Td>
                    </Table.Tr>
                  ))}
                  <Table.Tr>
                    <Table.Td colSpan={4}>
                      <Button fullWidth variant="light" leftSection={<IconPlus size={14} />} onClick={() => setEditingStatusLog('new')} disabled={!writable}>
                        Add Status Log
                      </Button>
                    </Table.Td>
                  </Table.Tr>
                </Table.Tbody>
              </Table>
            )}
          </div>

          {/* ----------- Dangerous ----------- */}
          <div className="section">
            <div className="section-header danger">Dangerous</div>
            {needsSaveFirst ? saveFirstNotice : (
              <div className="section-body">
                <Text size="sm">Permanently delete this equipment and its status log entries. Attached accessories and consumables are NOT deleted — they remain in the global Accessories / Consumables lists.</Text>
                <Button color="red" variant="filled" onClick={() => setDangerOpen(true)} disabled={!admin} data-tour="equipment-delete">
                  Delete Equipment
                </Button>
                {!admin && <Text size="xs" c="dimmed">Only admins can delete equipment.</Text>}
              </div>
            )}
          </div>

          {err && <Alert color="red">{err}</Alert>}
          <Group justify="flex-end" mt="md">
            <Button variant="default" onClick={confirmClose}>Close</Button>
            {writable && <Button onClick={save} loading={busy} data-tour="equipment-save">Save</Button>}
          </Group>

          {id && selectingAccessory && (
            <SelectAccessoryModal
              equipmentId={id}
              attachedIds={data?.accessories.map((a) => a.id) ?? []}
              onClose={() => setSelectingAccessory(false)}
              onDone={async () => { await refresh(); setSelectingAccessory(false); }}
            />
          )}
          {editingAccessoryId !== null && (
            <AccessoryModal
              id={editingAccessoryId}
              onClose={() => setEditingAccessoryId(null)}
              onSaved={async () => { await refresh(); setEditingAccessoryId(null); }}
              onDeleted={async () => { await refresh(); setEditingAccessoryId(null); }}
            />
          )}
          {id && selectingConsumable && (
            <SelectConsumableModal
              equipmentId={id}
              attachedIds={data?.consumables.map((c) => c.id) ?? []}
              onClose={() => setSelectingConsumable(false)}
              onDone={async () => { await refresh(); setSelectingConsumable(false); }}
            />
          )}
          {id && editingStatusLog !== null && (
            <StatusLogModal
              equipmentId={id}
              initial={editingStatusLog === 'new' ? null : editingStatusLog}
              onClose={() => setEditingStatusLog(null)}
              onDone={async () => { await refresh(); setEditingStatusLog(null); }}
            />
          )}
          {id && dangerOpen && data && (
            <DangerousDeleteModal
              equipmentId={id}
              equipmentName={data.name}
              onClose={() => setDangerOpen(false)}
              onDeleted={() => { setDangerOpen(false); onDeleted(); }}
            />
          )}
        </Stack>
      )}
    </Modal>
  );
}
